import { Banknote, CalendarCheck2, Check, CheckCircle2, CircleDollarSign, Landmark, MailWarning, PartyPopper, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";
import type { DashboardData, Member, Speaker } from "../types";
import type { Page } from "./AppShell";
import { StatusCard } from "./StatusCard";
import { MemberPhoto } from "./MemberPhoto";
import { CommunicationHealthPanel } from "./CommunicationHealthPanel";
import { useState } from "react";

const memberMap = (members: Member[]) => new Map(members.map((member) => [member.id, member]));
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const meetingDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

export const attentionDestination = (area: DashboardData["attention"][number]["area"]): Page => area === "BANKING"
  ? "checking"
  : area === "DUES"
    ? "dues"
    : area === "RENEWALS"
      ? "renewals"
      : "speakers";

export const attentionLabel = (item: DashboardData["attention"][number]) => item.recommendation?.label ?? (item.area === "BANKING"
  ? "Open Chapter Checking"
  : item.area === "DUES"
    ? "Open Chapter Dues"
    : item.area === "RENEWALS"
      ? "Open Member Renewals"
      : "Open Meeting Prep");

export const chapterMeetingDayCopy = (value: string) => {
  const meetingDay = meetingDays.find((day) => day.toLowerCase() === value.trim().toLowerCase());
  return meetingDay
    ? { eyebrow: `${meetingDay} readiness`, sentence: `You’re ready for ${meetingDay}.` }
    : { eyebrow: "Chapter readiness", sentence: "You’re ready for your Chapter meeting." };
};

export function FeaturedSpeakerList({ speakers, members }: { speakers: Speaker[]; members: Map<string, Member> }) {
  return <div className="speaker-grid" data-speaker-count={speakers.length}>
    {speakers.map((speaker) => {
      const member = members.get(speaker.memberId);
      return <div className="speaker" key={speaker.id}>
        <MemberPhoto member={member} className="member-photo" eager />
        <div><strong>{member?.fullName}</strong><span>{member?.classification}</span><small className={speaker.status === "CONFIRMED" ? "confirmed" : "awaiting"}>{speaker.status === "CONFIRMED" ? "✓ Assigned · calendar acknowledged" : speaker.replacementOffer?.emergency ? "● Emergency fill-in response pending" : speaker.replacementOffer ? "● Replacement needs review" : "● Assigned Featured Speaker"}</small></div>
      </div>;
    })}
  </div>;
}

interface Props {
  data: DashboardData;
  setPage: (page: Page) => void;
  resolving: string | null;
  onResolve: (id: string) => void;
}

export function HomePage({ data, setPage, resolving, onResolve }: Props) {
  const [communicationBlockedCount, setCommunicationBlockedCount] = useState<number>();
  const meetingDay = chapterMeetingDayCopy(data.chapter.meetingDay);
  const members = memberMap(data.members);
  const nextMeeting = data.meetings[0];
  const directorMeeting = data.meetings.find((meeting) => meeting.type === "DIRECTOR_PRESENTATION");
  const difference = Math.abs(data.banking.bankBalance - data.banking.platformBalance);
  const checkingReady = data.reconciliation?.ready ?? difference === 0;
  const currentPct = data.chapter.memberCount ? Math.round((data.dues.current / data.chapter.memberCount) * 100) : 0;
  const openAttention = (item: (typeof data.attention)[number]) => item.recommendation?.targetDate
    ? onResolve(item.id)
    : setPage(attentionDestination(item.area));
  return (
    <>
      <div className="hero">
        <div>
          <p className="eyebrow">{meetingDay.eyebrow}</p>
          <h1>Good morning, {data.chapter.name}! <span>👋</span></h1>
          <p>{meetingDay.sentence} Here’s everything that matters this week.</p>
        </div>
        <div className={communicationBlockedCount ? "ready-badge ready-badge-alert" : "ready-badge"}>{communicationBlockedCount ? <MailWarning /> : <CheckCircle2 />}<span><strong>{communicationBlockedCount ? "Action Needed" : "Chapter Ready"}</strong><small>{communicationBlockedCount ? `${communicationBlockedCount} Member email warning${communicationBlockedCount === 1 ? "" : "s"}` : "Systems synced today"}</small></span></div>
      </div>

      <CommunicationHealthPanel onStatus={setCommunicationBlockedCount} />

      <div className="dashboard-grid">
        <StatusCard eyebrow="Featured Speakers" icon={<CalendarCheck2 />} action="View rotation" onAction={() => setPage("speakers")} className="speakers-card">
          <FeaturedSpeakerList speakers={nextMeeting.speakers} members={members} />
          {directorMeeting ? <div className="director-next"><span>Next {directorMeeting.directorPresentation?.label || "Director Presentation"}</span><strong>{format(parseISO(directorMeeting.date), "MMMM d")} · {directorMeeting.directorPresentation?.directorName}</strong></div> : null}
        </StatusCard>

        <StatusCard eyebrow="Member Renewals" icon={<PartyPopper />} action="Manage renewals" onAction={() => setPage("renewals")}>
          <div className="big-stat">{data.renewals.length}<span>pending renewal</span></div>
          <div className="compact-list">{data.renewals.slice(0, 2).map((renewal) => <div key={renewal.id}><strong>{members.get(renewal.memberId)?.fullName}</strong><span>{renewal.lifecycleStatus === "PAST_DUE" ? "Past due" : renewal.lifecycleStatus === "DUE_NOW" ? "Due now" : format(parseISO(renewal.renewalDate), "MMM d")}</span></div>)}</div>
        </StatusCard>

        <StatusCard eyebrow="Chapter Dues" icon={<CircleDollarSign />} action="Review" onAction={() => setPage("dues")}>
          <div className="progress-row"><div className="ring" style={{ "--progress": `${currentPct * 3.6}deg` } as React.CSSProperties}><strong>{currentPct}%</strong><span>current</span></div><div className="dues-breakdown"><span><b>{data.chapter.memberCount}</b> Members</span><span className="good"><b>{data.dues.current}</b> Current</span><span><b>{data.dues.due}</b> Due</span><span className="danger"><b>{data.dues.needsAttention}</b> Needs Attention</span></div></div>
        </StatusCard>

        <StatusCard eyebrow="Chapter Checking" icon={<Landmark />} action="Open checking" onAction={() => setPage("checking")}>
          <div className="balance"><span>Bank balance</span><strong>{money.format(data.banking.bankBalance)}</strong></div>
          <div className={checkingReady ? "reconciled" : "reconciled alert"}><Check size={17} /><span><strong>{checkingReady ? "Reconciled" : "Needs review"}</strong><small>{data.reconciliation?.reasons[0] ?? `Platform balance ${money.format(data.banking.platformBalance)} · Synced today`}</small></span></div>
        </StatusCard>
      </div>

      <section className="attention-box">
        <div className="attention-title"><span className="magic-icon"><Sparkles /></span><div><p className="eyebrow">The Magic Box</p><h2>{data.attention.length ? "Needs Your Attention" : "You’re All Caught Up!"}</h2><p>{data.attention.length ? `${data.attention.length} things need you. Everything else looks great.` : "Your Chapter is ready."}</p></div><div className="attention-count">{data.attention.length}</div></div>
        <div className="attention-list">
          {data.attention.map((item) => <article key={item.id} className={`attention-item severity-${item.severity.toLowerCase()}`}>
            <span className="area-icon">{item.area === "BANKING" ? <Banknote /> : item.area === "RENEWALS" ? <PartyPopper /> : item.area === "DUES" ? <CircleDollarSign /> : <CalendarCheck2 />}</span>
            <div><small>{item.area}</small><strong>{item.title}</strong><p>{item.description}</p></div>
            <button onClick={() => openAttention(item)} disabled={resolving === item.id}>{resolving === item.id ? "Working…" : attentionLabel(item)}</button>
          </article>)}
          {!data.attention.length ? <div className="all-clear"><CheckCircle2 /><strong>Nothing needs your attention.</strong><span>Speakers, renewals, Chapter Dues, and checking are all handled.</span></div> : null}
        </div>
      </section>
    </>
  );
}
